import * as buildingscore from '@/api-client/models/buildingScore/Contracts'
import * as utilization from '@/api-client/models/utilization/Contracts'
import * as indoorclimate from '@/api-client/models/indoorclimate/Contracts'
import * as shared from '@/api-client/models/shared/Contracts'
import * as energy from '@/api-client/models/energy/Contracts'
import * as tickets from '@/api-client/models/tickets/Contracts'
import { CalendarFilters, FeatureFilters, FeatureType } from '@/composables/useFilters'
import { resolutionFromTimespan } from '@/utils/resolution-from-timespan'
import * as project from '@/api-client/models/project/Contracts'
import * as portfolio from '@/api-client/models/portfolio/Contracts'
import { differenceInDays, subDays } from 'date-fns'
import { LevelOfDetail } from './pinia/levelOfDetail'
import { Page } from './services/tickets.service'
import { roomTypeFiltersToCoclasses } from './components/types'

export type FetchTypes = {
  'fetch.information': {
    filter: FeatureType
    query:
      | buildingscore.InformationQuery
      | shared.InformationQuery
      | project.InformationQuery
      | portfolio.InformationQuery
    response:
      | buildingscore.InformationResponse
      | shared.AnyInformationResponse
      | project.InformationResponse
      | portfolio.InformationResponse
  }
  'fetch.buildingscore.information': {
    filter: FeatureType.BuildingScore
    query: buildingscore.InformationQuery
    response: buildingscore.InformationResponse
  }
  'fetch.buildingscore.kpi': {
    filter: FeatureType.BuildingScore
    query: buildingscore.KpiQuery
    response: buildingscore.KpiResponse
  }
  'fetch.buildingscore.timeseries': {
    filter: FeatureType.BuildingScore
    query: buildingscore.TimeSeriesQuery
    response: buildingscore.TimeSeriesResponse
  }
  'fetch.utilization.information': {
    filter: FeatureType.Utilization
    query: shared.InformationQuery
    response: shared.AnyInformationResponse
  }
  'fetch.utilization.kpi': {
    filter: FeatureType.Utilization
    query: utilization.KpiQuery
    response: utilization.KpiResponse
  }
  'fetch.utilization.timeseries': {
    filter: FeatureType.Utilization
    query: utilization.TimeSeriesQuery
    response: utilization.TimeSeriesResponse
  }
  'fetch.utilization.quota': {
    filter: FeatureType.Utilization
    query: utilization.QuotaQuery
    response: utilization.QuotaResponse
  }
  'fetch.utilization.heatmap': {
    filter: FeatureType.Utilization
    query: utilization.HeatmapQuery
    response: utilization.HeatmapResponse
  }
  'fetch.utilization.toplist': {
    filter: FeatureType.Utilization
    query: utilization.ToplistQuery
    response: utilization.ToplistResponse
  }
  'fetch.indoorclimate.information': {
    filter: FeatureType.IndoorClimate
    query: shared.InformationQuery
    response: shared.AnyInformationResponse
  }
  'fetch.ticket.measurements': {
    filter: FeatureType.TicketAnalysis
    query: tickets.KpiQuery
    response: tickets.KpiResponse
  }
  'fetch.ticket.timeseries': {
    filter: FeatureType.TicketAnalysis
    query: tickets.FilterQuery
    response: tickets.TimeSeriesResponse
  }
  'fetch.ticket.measurements.detail': {
    filter: FeatureType.TicketAnalysis
    query: tickets.KpiDetailQuery
    response: tickets.KpiResponse
  }
  'fetch.ticket.list': {
    filter: FeatureType.TicketAnalysis
    query: tickets.FilterQuery
    response: Page<tickets.Ticket>
  }
  'fetch.portfolio.area.information': {
    filter: FeatureType.PortfolioAnalysis
    query: portfolio.InformationQuery
    response: portfolio.InformationResponse
  }
  'fetch.energy.actual.information': {
    filter: FeatureType.EnergyAnalysis
    query: shared.InformationQuery
    response: shared.AnyInformationResponse
  }
  'fetch.portfolio.apartments.information': {
    filter: FeatureType.PortfolioAnalysis
    query: portfolio.InformationQuery
    response: portfolio.InformationResponse
  }
  'fetch.indoorclimate.temperature.kpi': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.KpiQuery
    response: indoorclimate.KpiResponse
  }
  'fetch.indoorclimate.temperature.toplist.average': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.ToplistQuery
    response: indoorclimate.ToplistResponse
  }
  'fetch.indoorclimate.co2.toplist.average': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.ToplistQuery
    response: indoorclimate.ToplistResponse
  }
  'fetch.indoorclimate.humidity.toplist.average': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.ToplistQuery
    response: indoorclimate.ToplistResponse
  }
  'fetch.indoorclimate.index.kpi': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.KpiQuery
    response: indoorclimate.KpiResponse
  }
  'fetch.indoorclimate.index.detail': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.KpiQuery
    response: indoorclimate.KpiResponse
  }
  'fetch.indoorclimate.index.heatmap': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.HeatmapQuery
    response: indoorclimate.HeatmapResponse
  }
  'fetch.indoorclimate.temperature.timeseries': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.TimeSeriesQuery
    response: indoorclimate.TimeSeriesResponse
  }
  'fetch.indoorclimate.humidity.kpi': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.KpiQuery
    response: indoorclimate.KpiResponse
  }
  'fetch.indoorclimate.humidity.timeseries': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.TimeSeriesQuery
    response: indoorclimate.TimeSeriesResponse
  }
  'fetch.indoorclimate.co2.kpi': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.KpiQuery
    response: indoorclimate.KpiResponse
  }
  'fetch.indoorclimate.co2.timeseries': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.TimeSeriesQuery
    response: indoorclimate.TimeSeriesResponse
  }
  'fetch.indoorclimate.temperature.histogram': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.HistogramQuery
    response: indoorclimate.HistogramResponse
  }
  'fetch.indoorclimate.co2.histogram': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.HistogramQuery
    response: indoorclimate.HistogramResponse
  }
  'fetch.indoorclimate.humidity.histogram': {
    filter: FeatureType.IndoorClimate
    query: indoorclimate.HistogramQuery
    response: indoorclimate.HistogramResponse
  }
  'fetch.project.measurements': {
    filter: FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal
    query: project.MeasurementQuery
    response: project.MeasurementResponse
  }
  'fetch.project.measurements.detail': {
    filter: FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal
    query: project.MeasurementQuery
    response: project.MeasurementResponse
  }
  'fetch.portfolio.apartments.measurements': {
    filter: FeatureType.PortfolioAnalysis
    query: portfolio.MeasurementQuery
    response: portfolio.MeasurementResponse
  }
  'fetch.portfolio.apartments.histograms': {
    filter: FeatureType.PortfolioAnalysis
    query: portfolio.HistogramQuery
    response: portfolio.HistogramResponse
  }
  'fetch.energy.measurements': {
    filter: FeatureType.EnergyAnalysis
    query: portfolio.MeasurementQuery
    response: portfolio.MeasurementResponse
  }
  'fetch.energy.actual.measurements': {
    filter: FeatureType.EnergyAnalysis
    query: energy.KpiQuery
    response: energy.KpiResponse
  }
  'fetch.energy.actual.total.detail': {
    filter: FeatureType.EnergyAnalysis
    query: energy.KpiQuery
    response: energy.KpiResponse
  }
  'fetch.energy.actual.totalOverArea.detail': {
    filter: FeatureType.EnergyAnalysis
    query: energy.KpiQuery
    response: energy.KpiResponse
  }
  'fetch.energy.actual.bought.detail': {
    filter: FeatureType.EnergyAnalysis
    query: energy.KpiQuery
    response: energy.KpiResponse
  }
  'fetch.energy.actual.boughtOverArea.detail': {
    filter: FeatureType.EnergyAnalysis
    query: energy.KpiQuery
    response: energy.KpiResponse
  }
  'fetch.energy.actual.production.detail': {
    filter: FeatureType.EnergyAnalysis
    query: energy.KpiQuery
    response: energy.KpiResponse
  }
  'fetch.energy.actual.productionOverArea.detail': {
    filter: FeatureType.EnergyAnalysis
    query: energy.KpiQuery
    response: energy.KpiResponse
  }
  'fetch.energy.timeseries': {
    filter: FeatureType.EnergyAnalysis
    query: energy.TimeSeriesQuery
    response: energy.TimeSeriesResponse
  }
  'fetch.portfolio.area.measurements': {
    filter: FeatureType.PortfolioAnalysis
    query: portfolio.MeasurementQuery
    response: portfolio.MeasurementResponse
  }
  'fetch.portfolio.area.heatmap': {
    filter: FeatureType.PortfolioAnalysis
    query: portfolio.HeatmapQuery
    response: portfolio.HeatmapResponse
  }
  'fetch.portfolio.premises.measurements': {
    filter: FeatureType.PortfolioAnalysis
    query: portfolio.MeasurementQuery
    response: portfolio.MeasurementResponse
  }
  'fetch.aggregate.measurements.climate': {
    filter: FeatureType.AggregateAnalysis
    query: any
    response: any
  }
}

export type FetchType = keyof FetchTypes
export type FetchQuery<T extends FetchType> = FetchTypes[T]['query']
export type FetchResponse<T extends FetchType> = FetchTypes[T]['response']
export type FetchFilter<T extends FetchType> = FetchTypes[T]['filter']

export type FetchConfig<Query> = {
  path: string
  method?: 'GET' | 'POST'
  queryParams?: Record<string, string | number | boolean>
  query: Query | null
}

export type Fetches = {
  [key in FetchType]: (
    filters: FeatureFilters<FetchTypes[key]['filter']>,
    parameters?: any
  ) => FetchConfig<FetchQuery<key>>
}

const calculateBuildingScoreFilterQuery = (
  filter: FeatureFilters<FeatureType.BuildingScore>
): buildingscore.FilterQuery => {
  const complexId = filter.complex?.belongsToId
  const constructions = filter.buildings.map((b) => b.id)
  return {
    complexes: complexId ? [complexId] : undefined,
    constructions: constructions.length > 0 ? constructions : undefined
  }
}

const calculateBuildingScoreInformationQuery = (
  filter: FeatureFilters<FeatureType.BuildingScore>
): buildingscore.InformationQuery | null => {
  return {
    year: filter.year,
    ...calculateBuildingScoreFilterQuery(filter)
  }
}

export const calculateEntityType = (
  filter: FeatureFilters | undefined,
  includeRealEstate = false
): shared.CoreType | undefined => {
  if (!filter) return undefined
  if ('room' in filter && filter.room) {
    return 'SPACE'
  } else if ('level' in filter && filter.level) {
    return 'LEVEL'
  } else if (filter.buildings && filter.buildings.length > 0) {
    return 'CONSTRUCTION'
  } else if ('realEstate' in filter && filter.realEstate && includeRealEstate) {
    return 'REAL_ESTATE'
  } else if (filter.complex) {
    return 'COMPLEX'
  }
  return undefined
}

export const calculateEntityIds = (
  filter: FeatureFilters,
  includeRealEstate: boolean = false
): string[] => {
  if ('room' in filter && filter.room) {
    return [filter.room.id]
  } else if ('level' in filter && filter.level) {
    return [filter.level.id]
  } else if (filter.buildings && filter.buildings.length > 1) {
    return filter.buildings.map((b) => b.id)
  } else if (filter.buildings.length === 1) {
    return [filter.buildings[0].id]
  } else if ('realEstate' in filter && filter.realEstate && includeRealEstate) {
    return [filter.realEstate.belongsToId]
  } else if (filter.complex) {
    return [filter.complex.belongsToId]
  } else {
    return []
  }
}

export const calculateDefaultFilterQuery = (
  filter: FeatureFilters<
    FeatureType.Utilization | FeatureType.IndoorClimate | FeatureType.EnergyAnalysis
  >
): shared.FilterQueryDefault | null => {
  if (!('calendar' in filter)) return null
  const from = filter.calendar?.from
  const to = filter.calendar?.to
  const entityType = calculateEntityType(filter)
  const entityIds = calculateEntityIds(filter)

  if (!entityType || !from || !to) return null

  const coClasses =
    'roomTypes' in filter && filter.roomTypes.length > 0
      ? roomTypeFiltersToCoclasses(filter.roomTypes)
      : undefined
  const numberOfSeats =
    'roomCapacity' in filter && filter.roomCapacity.length > 0
      ? filter.roomCapacity.map((capacity) => capacity.value).flat()
      : undefined
  const onlyBookable = 'onlyBookable' in filter ? filter.onlyBookable : undefined

  return {
    from,
    to,
    entityType,
    entityIds,
    onlyBusinessHours: filter.onlyBusinessHours ?? false,
    onlyBusinessDays: filter.onlyBusinessDays ?? false,
    onlyBookable: onlyBookable,
    coClasses,
    numberOfSeats
  }
}

const calculateBuildingScoreKpiQuery = (
  filter: FeatureFilters<FeatureType.BuildingScore>
): buildingscore.KpiQuery | null => {
  const kpis: buildingscore.Kpi[] = [
    {
      kpi: 'BUILDING_SCORE',
      splitBy: 'CONSTRUCTION',
      parameters: { year: filter.year }
    },
    {
      kpi: 'TREND',
      splitBy: 'CONSTRUCTION',
      parameters: {
        type: 'ATTRACTIVENESS',
        from: filter.year - 1,
        to: filter.year
      }
    }
  ]

  return {
    filter: calculateBuildingScoreFilterQuery(filter),
    kpis
  }
}

const calculateBuildingScoreTimeSeriesQuery = (
  filter: FeatureFilters<FeatureType.BuildingScore>
): buildingscore.TimeSeriesQuery | null => {
  return {
    filter: calculateBuildingScoreFilterQuery(filter)
  }
}

const getSplitBy = (coreType: shared.CoreType): shared.CoreType => {
  switch (coreType) {
    case 'SPACE':
      return 'SPACE'
    case 'LEVEL':
      return 'SPACE'
    case 'CONSTRUCTION':
      return 'LEVEL'
    case 'COMPLEX':
      return 'CONSTRUCTION'
    case 'REAL_ESTATE':
      return 'CONSTRUCTION'
  }
}

const getSplitByWithLod = (
  coreType: shared.CoreType,
  levelOfDetail: LevelOfDetail
): shared.CoreType => {
  switch (coreType) {
    case 'SPACE':
      return 'SPACE'
    case 'LEVEL':
      return 'SPACE'
    case 'CONSTRUCTION':
      return levelOfDetail
    case 'COMPLEX':
      return 'CONSTRUCTION'
    case 'REAL_ESTATE':
      return 'CONSTRUCTION'
  }
}

const calculateInformationQuery = (
  filter: FeatureFilters<
    FeatureType.Utilization | FeatureType.IndoorClimate | FeatureType.EnergyAnalysis
  >
): shared.InformationQuery | null => {
  const defaultFilter = calculateDefaultFilterQuery(filter)
  if (!defaultFilter) return null
  const from = defaultFilter.from
  const to = defaultFilter.to
  let fromDate = new Date(from)
  const toDate = new Date(to)

  const days = differenceInDays(toDate, fromDate)

  if (days >= 7) {
    fromDate = subDays(toDate, 7)
  }

  const buildingIds = filter.buildings.map((b) => b.id)
  return {
    complexIds: filter.complex ? [filter.complex.belongsToId] : undefined,
    constructionIds: buildingIds.length > 0 ? buildingIds : undefined,
    levelIds: 'level' in filter && filter.level ? [filter.level.id] : undefined,
    from: fromDate.toISOString().split('T')[0],
    to: to,
    onlyBusinessDays: defaultFilter.onlyBusinessDays,
    onlyBusinessHours: defaultFilter.onlyBusinessHours,
    onlyBookable: defaultFilter.onlyBookable,
    coClasses: defaultFilter.coClasses,
    numberOfSeats: defaultFilter.numberOfSeats
  }
}
const calculateInforamtionQueryProject = (
  filter: FeatureFilters<FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal>
): project.InformationQuery | null => {
  const buildingIds = filter.buildings.map((b) => b.id)
  return {
    complexIds: filter.complex ? [filter.complex.belongsToId] : undefined,
    constructionIds: buildingIds.length > 0 ? buildingIds : undefined,
    types: filter.projectTypes,
    statuses: filter.projectStatuses,
    budget: {
      from: filter.budget?.from ? filter.budget.from * 1000 : undefined,
      to: filter.budget?.to ? filter.budget.to * 1000 : undefined
    },
    internal: filter.type === FeatureType.ProjectAnalysisInternal
  }
}

const calculateInforamtionQueryTickets = (
  filter: FeatureFilters<FeatureType.TicketAnalysis>
): shared.InformationQuery | null => {
  const buildingIds = filter.buildings.map((b) => b.id)

  if (!('calendar' in filter) || !filter.calendar?.from || !filter.calendar.to) {
    return null
  }

  return {
    from: filter.calendar.from,
    to: filter.calendar.to,
    complexIds: filter.complex ? [filter.complex.belongsToId] : undefined,
    constructionIds: buildingIds.length > 0 ? buildingIds : undefined,
    levelIds: filter.level ? [filter.level.id] : undefined,
    onlyBusinessDays: false,
    onlyBusinessHours: false
  }
}

const calculateInforamtionQueryPortfolio = (
  filter: FeatureFilters<FeatureType.PortfolioAnalysis | FeatureType.EnergyAnalysis>
): portfolio.InformationQuery | null => {
  const buildingIds = filter.buildings.map((b) => b.id)
  return {
    complexIds: filter.complex ? [filter.complex.belongsToId] : undefined,
    realEstateIds: filter.realEstate ? [filter.realEstate.belongsToId] : undefined,
    constructionIds: buildingIds.length > 0 ? buildingIds : undefined
  }
}

const calculateUtilizationKpiQuery = (
  filter: FeatureFilters<FeatureType.Utilization>,
  kpis: utilization.Kpi[]
): utilization.KpiQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null
  return {
    filter: filterQuery,
    kpis
  }
}

const calculateUtilizationTimeseriesQuery = (
  filter: FeatureFilters<FeatureType.Utilization>
): utilization.TimeSeriesQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null
  const resolution = resolutionFromTimespan(filter.calendar?.from, filter.calendar?.to)
  return {
    filter: filterQuery,
    resolution: resolution
  }
}

const calculateUtilizationQuotaQuery = (
  filter: FeatureFilters<FeatureType.Utilization>
): utilization.QuotaQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null
  return {
    filter: filterQuery
  }
}

const calculateUtilizationHeatmapQuery = (
  filter: FeatureFilters<FeatureType.Utilization>
): utilization.HeatmapQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null
  return {
    filter: filterQuery
  }
}

const calculateUtilizationToplistQuery = (
  filter: FeatureFilters<FeatureType.Utilization>
): utilization.ToplistQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null
  return {
    toplistOf: 'SPACE',
    filter: filterQuery
  }
}

const calculateIndoorClimateToplistQuery = (
  filter: FeatureFilters<FeatureType.IndoorClimate>,
  measurementTypes: indoorclimate.MeasurementType[],
  sortBy: indoorclimate.MeasurementType,
  sortOrder: 'ASCENDING' | 'DESCENDING',
  limit: number
): indoorclimate.ToplistQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null

  const entityType = filterQuery.entityType
  const splitBy = getSplitBy(entityType)
  return {
    filter: filterQuery,
    measurementTypes,
    sortBy,
    sortOrder,
    splitBy,
    limit
  } as indoorclimate.ToplistQuery
}

export const calculateIndoorClimateKpiQuerySimple = (
  filter: FeatureFilters<FeatureType.IndoorClimate>,
  ...kpiTypes: indoorclimate.KpiType[]
): indoorclimate.KpiQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    kpis: kpiTypes.map((type) => ({ type }))
  }
}

export const calculateIndoorClimateKpiQuery = (
  filter: FeatureFilters<FeatureType.IndoorClimate>,
  kpiType: indoorclimate.KpiType,
  ...subKpiTypes: indoorclimate.KpiType[]
): indoorclimate.KpiQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null

  const entityType = filterQuery.entityType
  const splitBy = getSplitByWithLod(entityType, filter.levelOfDetail)

  return {
    filter: filterQuery,
    kpis: [
      {
        type: kpiType
      },
      {
        type: kpiType,
        splitBy
      },
      ...(subKpiTypes
        ? subKpiTypes.map((subKpiType) => ({
            type: subKpiType
          }))
        : [])
    ]
  }
}

const getSplitByHeatmap = (coreType: shared.CoreType): indoorclimate.HeatmapSplitBy => {
  switch (coreType) {
    case 'SPACE':
      return 'SUB_MEASUREMENT'
    case 'LEVEL':
      return 'SPACE'
    case 'CONSTRUCTION':
      return 'LEVEL'
    case 'COMPLEX':
      return 'CONSTRUCTION'
    case 'REAL_ESTATE':
      return 'CONSTRUCTION'
  }
}

const calculateIndoorClimateHeatmapQuery = (
  filter: FeatureFilters<FeatureType.IndoorClimate>
): indoorclimate.HeatmapQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    splitBy: getSplitByHeatmap(filterQuery.entityType)
  }
}

const trimDateString = (dateString: string): string => {
  return dateString.replace(/T.*/, '')
}

const calculateIndoorClimateTimeSeriesQuery = (
  filter: FeatureFilters<FeatureType.IndoorClimate>,
  source: indoorclimate.DataSourceType
): indoorclimate.TimeSeriesQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null

  const bucketSize = filter.bucketSize!

  return {
    filter: {
      ...filterQuery,
      from: trimDateString(filterQuery.from),
      to: trimDateString(filterQuery.to)
    },
    splits: [
      {
        aggregations: [
          {
            source,
            bucketSize,
            bucketAggregationMethod: 'MEAN',
            spaceAggregationMethod: 'MEAN'
          }
        ]
      }
    ]
  }
}

const calculateIndoorClimateHistogramQuery = (
  filter: FeatureFilters<FeatureType.IndoorClimate>,
  source: indoorclimate.DataSourceType,
  min: number,
  max: number,
  bucketSize: number
): indoorclimate.HistogramQuery | null => {
  const filterQuery = calculateDefaultFilterQuery(filter)
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    histograms: [
      {
        source,
        sourceType: 'MEAN',
        bucketSize,
        min,
        max
      }
    ]
  }
}

function calculateProjectFilterQuery(
  filter: FeatureFilters<FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal>
): project.FilterQuery | null {
  const entityType = calculateEntityType(filter)
  const entityIds = calculateEntityIds(filter)

  const types = filter.projectTypes
  const statuses = filter.projectStatuses
  const budget = {
    from: filter.budget?.from ? filter.budget.from * 1000 : undefined,
    to: filter.budget?.to ? filter.budget.to * 1000 : undefined
  }

  return {
    entityType,
    entityIds,
    statuses,
    types,
    budget
  }
}

function calculatePortfolioFilterQuery(
  filter: FeatureFilters<FeatureType.PortfolioAnalysis | FeatureType.EnergyAnalysis>
): portfolio.FilterQuery | null {
  const entityType = calculateEntityType(filter, true)
  const entityIds = calculateEntityIds(filter, true)

  return {
    entityType,
    entityIds
  }
}

function calculateEnergyActualFilterQuery(
  filter: FeatureFilters<FeatureType.EnergyAnalysis>
): shared.FilterQueryDefault | null {
  if (!('calendar' in filter) || !filter.calendar) return null
  const calendar = filter.calendar as CalendarFilters

  const entityType = calculateEntityType(filter, false)
  const entityIds = calculateEntityIds(filter, false)
  const from = calendar.from
  const to = calendar.to

  if (!entityType || !entityIds || !from || !to) return null

  return {
    entityType,
    entityIds,
    from,
    to,
    onlyBusinessDays: false,
    onlyBusinessHours: false
  }
}

function calculateProjectMeasurementQuery(
  filter: FeatureFilters<FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal>
): project.MeasurementQuery | null {
  const filterQuery = calculateProjectFilterQuery(filter)
  if (!filterQuery) return null

  const createStatusMeasurements = (status: project.ProjectStatus): project.Measurement[] => [
    {
      status
    },
    {
      status,
      splitBy: 'CONSTRUCTION'
    }
  ]

  return {
    filter: filterQuery,
    internal: filter.type === FeatureType.ProjectAnalysisInternal,
    measurements: [
      ...createStatusMeasurements('IN_PROGRESS'),
      ...createStatusMeasurements('PLANNED'),
      ...createStatusMeasurements('NOT_STARTED'),
      ...createStatusMeasurements('COMPLETED')
    ]
  }
}

function calculateTicketFilterQuery(
  filter: FeatureFilters<FeatureType.TicketAnalysis>
): tickets.FilterQuery | null {
  if (!('calendar' in filter) || !filter.calendar) return null
  const calendar = filter.calendar
  const entityType = calculateEntityType(filter, false)
  const entityIds = calculateEntityIds(filter, false)
  const from = calendar.from
  const to = calendar.to
  const ticketTypeIds = filter.ticketTypes.length > 0 ? filter.ticketTypes : undefined
  const ticketStatuses = filter.ticketStatuses.length > 0 ? filter.ticketStatuses : undefined

  if (!entityType || !entityIds || !from || !to) return null

  return {
    entityType,
    entityIds,
    from: new Date(from).toISOString().split('T')[0],
    to: new Date(to).toISOString().split('T')[0],
    ticketTypeIds,
    ticketStatuses
  }
}

function calculateTicketMeasurementQuery(
  filter: FeatureFilters<FeatureType.TicketAnalysis>
): tickets.KpiQuery | null {
  const filterQuery = calculateTicketFilterQuery(filter)
  if (!filterQuery?.entityType) return null

  const splitBy = getSplitBy(filterQuery.entityType)

  return {
    filter: filterQuery,
    kpis: [
      {
        type: 'SUM_TICKETS'
      },
      {
        type: 'SUM_TICKETS',
        splitBy
      }
    ]
  }
}

function calculateTicketTimeseriesQuery(
  filter: FeatureFilters<FeatureType.TicketAnalysis>
): tickets.FilterQuery | null {
  const filterQuery = calculateTicketFilterQuery(filter)
  if (!filterQuery) return null

  return filterQuery
}

export function calculateTicketListQuery(
  filter: FeatureFilters<FeatureType.TicketAnalysis>
): tickets.FilterQuery | null {
  return calculateTicketTimeseriesQuery(filter)
}

function calculateTicketMeasurementDetailQuery(
  filter: FeatureFilters<FeatureType.TicketAnalysis>
): tickets.KpiDetailQuery | null {
  const filterQuery = calculateTicketFilterQuery(filter)
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    kpis: [
      {
        type: 'SUM_TICKETS',
        splitBy: 'TYPE'
      }
    ]
  }
}

function calculateProjectMeasurementDetailQuery(
  filter: FeatureFilters<FeatureType.ProjectAnalysisExternal | FeatureType.ProjectAnalysisInternal>
): project.MeasurementQuery | null {
  const filterQuery = calculateProjectFilterQuery(filter)
  if (!filterQuery) return null

  const types = filter.projectTypes

  const createStatusMeasurements = (status: project.ProjectStatus): project.Measurement[] =>
    types.map((type) => ({
      status,
      type
    }))

  return {
    filter: filterQuery,
    internal: filter.type === FeatureType.ProjectAnalysisInternal,
    measurements: [
      ...createStatusMeasurements('IN_PROGRESS'),
      ...createStatusMeasurements('PLANNED'),
      ...createStatusMeasurements('NOT_STARTED'),
      ...createStatusMeasurements('COMPLETED')
    ]
  }
}

function calculatePortfolioMeasurementQuery(
  filter: FeatureFilters<FeatureType.PortfolioAnalysis>,
  ...measurements: portfolio.MeasurementType[]
): portfolio.MeasurementQuery | null {
  const filterQuery = calculatePortfolioFilterQuery(filter)
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    measurements: [
      ...measurements.map((type) => ({ type })),
      ...(filterQuery.entityType
        ? measurements.map((type) => ({ type, splitBy: 'CONSTRUCTION' }))
        : [])
    ]
  }
}

function calculateEnergyMeasurementQuery(
  filter: FeatureFilters<FeatureType.EnergyAnalysis>
): portfolio.MeasurementQuery | null {
  const filterQuery = calculatePortfolioFilterQuery(filter)
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    measurements: [
      {
        type: 'DECLARATION_PERFORMED_DATE'
      },
      {
        type: 'ENERGY_CLASS'
      },
      {
        type: 'ENERGY_PERFORMANCE'
      },
      {
        type: 'SPECIFIC_ENERGY_CONSUMPTION'
      },
      {
        type: 'RADON_MEASUREMENT_PERFORMED'
      },
      {
        type: 'VENTILATION_CONTROL_PERFORMED'
      },
      {
        type: 'DECLARATION_PERFORMED_DATE',
        splitBy: 'CONSTRUCTION'
      },
      {
        type: 'ENERGY_CLASS',
        splitBy: 'CONSTRUCTION'
      },
      {
        type: 'ENERGY_PERFORMANCE',
        splitBy: 'CONSTRUCTION'
      },
      {
        type: 'SPECIFIC_ENERGY_CONSUMPTION',
        splitBy: 'CONSTRUCTION'
      },
      {
        type: 'RADON_MEASUREMENT_PERFORMED',
        splitBy: 'CONSTRUCTION'
      },
      {
        type: 'VENTILATION_CONTROL_PERFORMED',
        splitBy: 'CONSTRUCTION'
      }
    ]
  }
}

function calculateActualEnergyMeasurementQuery(
  filter: FeatureFilters<FeatureType.EnergyAnalysis>
): energy.KpiQuery | null {
  const filterQuery = calculateEnergyActualFilterQuery(filter) as shared.FilterQueryDefault
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    kpis: [
      {
        type: 'ENERGY_CONSUMPTION'
      },
      {
        type: 'ENERGY_CONSUMPTION_OVER_AREA'
      },
      { type: 'ENERGY_BOUGHT' },
      { type: 'ENERGY_BOUGHT_OVER_AREA' },
      { type: 'ENERGY_PRODUCTION_SOLAR_ELECTRICITY' },
      { type: 'ENERGY_PRODUCTION_SOLAR_ELECTRICITY_OVER_AREA' },
      {
        type: 'ENERGY_CONSUMPTION',
        splitBy: 'CONSTRUCTION'
      },
      {
        type: 'ENERGY_CONSUMPTION_OVER_AREA',
        splitBy: 'CONSTRUCTION'
      },
      { type: 'ENERGY_BOUGHT', splitBy: 'CONSTRUCTION' },
      { type: 'ENERGY_BOUGHT_OVER_AREA', splitBy: 'CONSTRUCTION' },
      { type: 'ENERGY_PRODUCTION_SOLAR_ELECTRICITY', splitBy: 'CONSTRUCTION' },
      { type: 'ENERGY_PRODUCTION_SOLAR_ELECTRICITY_OVER_AREA', splitBy: 'CONSTRUCTION' }
      //{ type: 'ENERGY_PRODUCTION_SOLAR', splitBy: 'CONSTRUCTION' }
    ]
  }
}

function calculateActualEnergyDetailsQuery(
  filter: FeatureFilters<FeatureType.EnergyAnalysis>,
  type: energy.EnergyType,
  overArea: boolean = false
): energy.KpiQuery | null {
  const filterQuery = calculateEnergyActualFilterQuery(filter) as shared.FilterQueryDefault
  if (!filterQuery) return null

  const kpis = energy.energyCategories[type].map(
    (suffix) => `ENERGY_${type}_${suffix}${overArea ? '_OVER_AREA' : ''}` as energy.KpiType
  )
  return {
    filter: filterQuery,
    kpis: kpis.map((type) => ({ type }))
  }
}

function calculatePortfolioAreaHeatmapQuery(
  filter: FeatureFilters<FeatureType.PortfolioAnalysis>
): portfolio.HeatmapQuery | null {
  const filterQuery = calculatePortfolioFilterQuery(filter)
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    splitBy: 'CONSTRUCTION'
  }
}

function calculatePortfolioHistogramQuery(
  filter: FeatureFilters<FeatureType.PortfolioAnalysis>
): portfolio.HistogramQuery | null {
  const filterQuery = calculatePortfolioFilterQuery(filter)
  if (!filterQuery) return null

  return {
    filter: filterQuery,
    histograms: [
      {
        type: 'TOTAL_NUMBER_OF_APARTMENTS',
        coDomain: 'COUNT',
        domain: 'NUMBER_OF_ROOMS'
      },
      {
        type: 'TOTAL_NUMBER_OF_APARTMENTS',
        coDomain: 'COUNT',
        domain: 'CATEGORY'
      }
    ]
  }
}

function calculatePath(filter: FeatureFilters<FeatureType.AggregateAnalysis>, depth = 0) {
  const complexId = filter.complex?.belongsToId
  const realEstateId = filter.realEstate?.belongsToId
  const constructionIds = filter.buildings.map((b) => b.id)
  const levelId = filter.level?.id
  const pathList = [complexId, realEstateId, ...constructionIds, levelId].filter(
    (id) => id !== undefined
  )
  const path = pathList.join('.') + '.*'
  if (pathList.length === 0) {
    return undefined
  }
  return [path, pathList.length + depth]
}

const fetches: Fetches = {
  'fetch.portfolio.apartments.information': (filter) => {
    return {
      query: calculateInforamtionQueryPortfolio(filter),
      path: '/core/v1/portfolio/information'
    }
  },
  'fetch.portfolio.area.information': (filter) => {
    return {
      query: calculateInforamtionQueryPortfolio(filter),
      path: '/core/v1/portfolio/area/information'
    }
  },
  'fetch.energy.actual.information': (filter) => {
    const q = calculateInformationQuery(filter)
    return {
      query: q
        ? ({
            ...q,
            realEstateIds: undefined
          } as unknown as shared.InformationQuery)
        : null,
      path: '/iot/v1/energy/information'
    }
  },
  'fetch.information': (filter) => {
    switch (filter.type) {
      case FeatureType.BuildingScore:
        return {
          query: calculateBuildingScoreInformationQuery(filter),
          path: '/iot/v1/buildingscore/information'
        }
      case 'utilization':
        return {
          query: calculateInformationQuery(filter),
          path: '/iot/v2/utilization/information',
          method: 'POST'
        }
      case 'indoor_climate':
        return {
          query: calculateInformationQuery(filter),
          path: '/iot/v1/indoorclimate/information'
        }
      case FeatureType.ProjectAnalysisInternal:
      case FeatureType.ProjectAnalysisExternal:
        return {
          query: calculateInforamtionQueryProject(filter),
          path: '/core/v1/projects/information'
        }
      case FeatureType.TicketAnalysis:
        return {
          query: calculateInforamtionQueryTickets(filter),
          path: '/iot/v1/tickets/information'
        }
      case FeatureType.PortfolioAnalysis:
        return {
          query: calculateInforamtionQueryPortfolio(filter),
          path: '/core/v1/portfolio/information'
        }
      case FeatureType.EnergyAnalysis:
        return {
          query: calculateInforamtionQueryPortfolio(filter),
          path: '/core/v1/portfolio/energy/information'
        }
    }
    throw new Error('Unknown filter type')
  },
  'fetch.buildingscore.information': (filter) => ({
    query: calculateBuildingScoreInformationQuery(filter),
    path: '/iot/v1/buildingscore/information'
  }),
  'fetch.buildingscore.kpi': (filter) => ({
    query: calculateBuildingScoreKpiQuery(filter),
    path: '/iot/v1/buildingscore/kpi'
  }),
  'fetch.buildingscore.timeseries': (filter) => ({
    query: calculateBuildingScoreTimeSeriesQuery(filter),
    path: '/iot/v2/buildingscore/timeseries'
  }),
  'fetch.utilization.information': (filter) => ({
    query: calculateInformationQuery(filter),
    method: 'POST',
    path: '/iot/v2/utilization/information'
  }),
  'fetch.utilization.kpi': (filter, parameters) => ({
    query: calculateUtilizationKpiQuery(filter, parameters),
    method: 'POST',
    path: '/iot/v2/utilization/kpi'
  }),
  'fetch.utilization.timeseries': (filter) => ({
    query: calculateUtilizationTimeseriesQuery(filter),
    method: 'POST',
    path: '/iot/v2/utilization/timeseries'
  }),
  'fetch.utilization.quota': (filter) => ({
    query: calculateUtilizationQuotaQuery(filter),
    method: 'POST',
    path: '/iot/v2/utilization/quota'
  }),
  'fetch.utilization.heatmap': (filter) => ({
    query: calculateUtilizationHeatmapQuery(filter),
    method: 'POST',
    path: '/iot/v2/utilization/heatmap'
  }),
  'fetch.utilization.toplist': (filter) => ({
    query: calculateUtilizationToplistQuery(filter),
    method: 'POST',
    path: '/iot/v2/utilization/toplist'
  }),
  'fetch.indoorclimate.information': (filter) => ({
    query: calculateInformationQuery(filter),
    path: '/iot/v1/indoorclimate/information'
  }),
  'fetch.indoorclimate.temperature.toplist.average': (filter) => ({
    query: calculateIndoorClimateToplistQuery(
      filter,
      ['TEMPERATURE_AVERAGE'],
      'TEMPERATURE_AVERAGE',
      'DESCENDING',
      10
    ),
    path: '/iot/v1/indoorclimate/toplist'
  }),
  'fetch.indoorclimate.co2.toplist.average': (filter) => ({
    query: calculateIndoorClimateToplistQuery(
      filter,
      ['CO2_AVERAGE'],
      'CO2_AVERAGE',
      'DESCENDING',
      10
    ),
    path: '/iot/v1/indoorclimate/toplist'
  }),
  'fetch.indoorclimate.humidity.toplist.average': (filter) => ({
    query: calculateIndoorClimateToplistQuery(
      filter,
      ['HUMIDITY_AVERAGE'],
      'HUMIDITY_AVERAGE',
      'DESCENDING',
      10
    ),
    path: '/iot/v1/indoorclimate/toplist'
  }),
  'fetch.indoorclimate.index.kpi': (filter) => ({
    query: calculateIndoorClimateKpiQuery(filter, 'ICI'),
    path: '/iot/v1/indoorclimate/kpi'
  }),
  'fetch.indoorclimate.index.detail': (filter) => ({
    query: calculateIndoorClimateKpiQuerySimple(
      filter,
      'ICI_TEMPERATURE_COMFORT_COOL',
      'ICI_TEMPERATURE_COMFORT_WARM',
      'ICI_TEMPERATURE_STABILITY',
      'ICI_CO2'
    ),
    path: '/iot/v1/indoorclimate/kpi'
  }),
  'fetch.indoorclimate.index.heatmap': (filter) => ({
    query: calculateIndoorClimateHeatmapQuery(filter),
    path: '/iot/v1/indoorclimate/heatmap'
  }),
  'fetch.indoorclimate.temperature.kpi': (filter) => ({
    query: calculateIndoorClimateKpiQuery(filter, 'TEMPERATURE_AVERAGE'),
    path: '/iot/v1/indoorclimate/kpi'
  }),
  'fetch.indoorclimate.temperature.timeseries': (filter) => ({
    query: calculateIndoorClimateTimeSeriesQuery(filter, 'TEMPERATURE'),
    path: '/iot/v1/indoorclimate/timeseries'
  }),
  'fetch.indoorclimate.humidity.kpi': (filter) => ({
    query: calculateIndoorClimateKpiQuery(filter, 'HUMIDITY_AVERAGE'),
    path: '/iot/v1/indoorclimate/kpi'
  }),
  'fetch.indoorclimate.humidity.timeseries': (filter) => ({
    query: calculateIndoorClimateTimeSeriesQuery(filter, 'HUMIDITY'),
    path: '/iot/v1/indoorclimate/timeseries'
  }),
  'fetch.indoorclimate.co2.kpi': (filter) => ({
    query: calculateIndoorClimateKpiQuery(filter, 'CO2_AVERAGE'),
    path: '/iot/v1/indoorclimate/kpi'
  }),
  'fetch.indoorclimate.co2.timeseries': (filter) => ({
    query: calculateIndoorClimateTimeSeriesQuery(filter, 'CO2'),
    path: '/iot/v1/indoorclimate/timeseries'
  }),
  'fetch.indoorclimate.temperature.histogram': (filter) => {
    return {
      query: calculateIndoorClimateHistogramQuery(filter, 'TEMPERATURE', 19, 28, 1),
      path: '/iot/v1/indoorclimate/histogram'
    }
  },
  'fetch.indoorclimate.co2.histogram': (filter) => {
    return {
      query: calculateIndoorClimateHistogramQuery(filter, 'CO2', 100, 900, 200),
      path: '/iot/v1/indoorclimate/histogram'
    }
  },
  'fetch.indoorclimate.humidity.histogram': (filter) => {
    return {
      query: calculateIndoorClimateHistogramQuery(filter, 'HUMIDITY', 0.0, 100.0, 10),
      path: '/iot/v1/indoorclimate/histogram'
    }
  },
  'fetch.ticket.measurements': (filter) => {
    return {
      query: calculateTicketMeasurementQuery(filter),
      path: '/iot/v1/tickets/measurements'
    }
  },
  'fetch.ticket.measurements.detail': (filter) => {
    return {
      query: calculateTicketMeasurementDetailQuery(filter),
      path: '/iot/v1/tickets/measurements/details'
    }
  },
  'fetch.ticket.timeseries': (filter) => {
    return {
      query: calculateTicketTimeseriesQuery(filter),
      path: '/iot/v1/tickets/timeseries'
    }
  },
  'fetch.ticket.list': (filter) => {
    return {
      query: calculateTicketListQuery(filter),
      queryParams: {
        page: 0,
        size: 100
      },
      path: '/iot/v1/tickets/list'
    }
  },
  'fetch.project.measurements': (filter) => {
    return {
      query: calculateProjectMeasurementQuery(filter),
      path: '/core/v1/projects/measurements'
    }
  },
  'fetch.project.measurements.detail': (filter) => {
    return {
      query: calculateProjectMeasurementDetailQuery(filter),
      path: '/core/v1/projects/measurements'
    }
  },
  'fetch.portfolio.apartments.measurements': (filter) => {
    return {
      query: calculatePortfolioMeasurementQuery(
        filter,
        'TOTAL_NUMBER_OF_APARTMENTS',
        'TOTAL_LIVING_AREA'
      ),
      path: '/core/v1/portfolio/measurements'
    }
  },
  'fetch.portfolio.apartments.histograms': (filter) => {
    return {
      query: calculatePortfolioHistogramQuery(filter),
      path: '/core/v1/portfolio/histogram'
    }
  },
  'fetch.energy.measurements': (filter) => {
    return {
      query: calculateEnergyMeasurementQuery(filter),
      path: '/core/v1/portfolio/measurements'
    }
  },
  'fetch.energy.actual.measurements': (filter) => {
    return {
      query: calculateActualEnergyMeasurementQuery(filter),
      path: '/iot/v1/energy/measurements'
    }
  },
  'fetch.energy.actual.total.detail': (filter) => {
    return {
      query: calculateActualEnergyDetailsQuery(filter, 'CONSUMPTION'),
      path: '/iot/v1/energy/measurements'
    }
  },
  'fetch.energy.actual.totalOverArea.detail': (filter) => {
    return {
      query: calculateActualEnergyDetailsQuery(filter, 'CONSUMPTION', true),
      path: '/iot/v1/energy/measurements'
    }
  },
  'fetch.energy.actual.bought.detail': (filter) => {
    return {
      query: calculateActualEnergyDetailsQuery(filter, 'BOUGHT'),
      path: '/iot/v1/energy/measurements'
    }
  },
  'fetch.energy.actual.boughtOverArea.detail': (filter) => {
    return {
      query: calculateActualEnergyDetailsQuery(filter, 'BOUGHT', true),
      path: '/iot/v1/energy/measurements'
    }
  },
  'fetch.energy.actual.production.detail': (filter) => {
    return {
      query: calculateActualEnergyDetailsQuery(filter, 'PRODUCTION'),
      path: '/iot/v1/energy/measurements'
    }
  },
  'fetch.energy.actual.productionOverArea.detail': (filter) => {
    return {
      query: calculateActualEnergyDetailsQuery(filter, 'PRODUCTION', true),
      path: '/iot/v1/energy/measurements'
    }
  },
  'fetch.energy.timeseries': (filter) => {
    const filterQuery = calculateEnergyActualFilterQuery(filter)
    return {
      query: filterQuery
        ? ({
            filter: filterQuery,
            kpis: [
              'ENERGY_BOUGHT_ELECTRICITY_OVER_AREA',
              'ENERGY_BOUGHT_DISTRICT_HEATING_OVER_AREA',
              'ENERGY_BOUGHT_COOLING_OVER_AREA'
            ] as energy.KpiType[]
          } as energy.TimeSeriesQuery)
        : null,
      path: '/iot/v1/energy/timeseries'
    }
  },
  'fetch.portfolio.premises.measurements': (filter) => {
    return {
      query: calculatePortfolioMeasurementQuery(
        filter,
        'AREA_EFFICIENCY',
        'AREA_LEASABLE_EFFICIENCY',
        'AREA_NON_RESIDENTIAL',
        'AREA_LEASABLE'
      ),
      path: '/core/v1/portfolio/measurements'
    }
  },
  'fetch.portfolio.area.measurements': (filter) => {
    return {
      query: calculatePortfolioMeasurementQuery(
        filter,
        'AREA_GROSS',
        'AREA_UTILITY',
        'AREA_BUILDING_FOOTPRINT',
        'AREA_NET'
      ),
      path: '/core/v1/portfolio/measurements'
    }
  },
  'fetch.portfolio.area.heatmap': (filter) => {
    return {
      query: calculatePortfolioAreaHeatmapQuery(filter),
      path: '/core/v1/portfolio/area/heatmap'
    }
  },
  'fetch.aggregate.measurements.climate': (filter) => {
    const createQuery = () => {
      const pathDepth = calculatePath(filter)
      const from = filter.calendar?.from ? new Date(filter.calendar.from).toISOString() : undefined
      const to = filter.calendar?.to ? new Date(filter.calendar.to).toISOString() : undefined
      if (!from || !to || !pathDepth) return null
      const [path, depth] = pathDepth
      return {
        query: {
          start: filter.calendar?.from,
          end: filter.calendar?.to,
          tags: [],
          pathQuery: path
        },
        depth
      }
    }

    return {
      query: createQuery(),
      method: 'POST',
      path: '/aggregate/v1/measurements/climate/aggregate'
    }
  }
}

export default fetches
